import type {
    BookPageLabels,
    ShareLabels,
    Course,
    NavItem,
    Service,
    SocialLink,
    Stat
} from '@/types'
import type { Locale } from '@/i18n/config'
import type { Messages } from '@/i18n/messages/sq'

import {
    AudioLines,
    BookOpen,
    Facebook,
    GraduationCap,
    Instagram,
    Languages,
    Mail,
    PenTool,
    PlayCircle,
    Send
} from 'lucide-react'

import { localeMeta } from '@/i18n/config'
import { messages } from '@/i18n'
import { sq } from '@/i18n/messages/sq'

/*
 * Structure of the portal: links, icons, images, names and addresses.
 * Every visible sentence lives in the dictionaries under src/i18n/messages,
 * one file per language; the helpers below merge the two for a given language.
 */

const navItems: { key: keyof Messages['nav']; href: string }[] = [
    { key: 'libra', href: '/libra' },
    { key: 'kurani', href: '/kurani' },
    { key: 'videoleksione', href: '/videoleksione' },
    { key: 'kurse', href: '/kurse' },
    { key: 'audiolibra', href: '/audiolibra' },
    { key: 'rrethNesh', href: '/rreth-nesh' },
    { key: 'kontakt', href: '/kontakt' }
]

export const getNav = (t: Messages): NavItem[] =>
    navItems.map((item) => ({ label: t.nav[item.key], href: item.href }))

/** Albanian navigation, for server code that has no language (llms.txt). */
export const nav: NavItem[] = getNav(sq)

const serviceItems: { id: keyof Messages['home']['services']['items']; icon: Service['icon']; href: string }[] = [
    { id: 'libra', icon: BookOpen, href: '/libra' },
    { id: 'videoleksione', icon: PlayCircle, href: '/videoleksione' },
    { id: 'kurse', icon: GraduationCap, href: '/kurse' },
    { id: 'audiolibra', icon: AudioLines, href: '/audiolibra' }
]

export const getServices = (t: Messages): (Service & { cta: string })[] =>
    serviceItems.map((item) => ({
        ...item,
        title: t.home.services.items[item.id].title,
        description: t.home.services.items[item.id].description,
        cta: t.home.services.items[item.id].cta
    }))

type CourseId = keyof Messages['courses']['items']

const courseItems: { id: CourseId; icon: Course['icon']; image?: string; instructor?: string }[] = [
    {
        id: 'shkronjat-e-kuranit',
        icon: PenTool,
        image: '/images/institute/kurs-femra.jpg',
        instructor: 'Salije Kastrati'
    },
    { id: 'leximi-me-texhvid', icon: BookOpen },
    { id: 'gjuha-arabe', icon: Languages }
]

export const getCourses = (t: Messages): Course[] =>
    courseItems.map((item) => {
        const copy = t.courses.items[item.id]
        return {
            ...item,
            title: copy.title,
            description: copy.description,
            level: copy.level,
            format: copy.format,
            badge: copy.badge || undefined,
            summary: copy.summary || undefined,
            cta: copy.cta
        }
    })

/** Albanian course titles: what the registration form stores, so the admin always reads one language. */
export const courseTitlesSq: Record<string, string> = Object.fromEntries(
    courseItems.map((item) => [item.id, sq.courses.items[item.id].title])
)

/** The director's name as written on each portal (Arabic script on the Arabic one). */
export const directorName = (locale: string): string => (locale === 'ar' ? 'يوسف كاستراتي' : 'Jusuf Kastrati')

export const director = {
    name: 'Jusuf Kastrati',
    photo: '/images/institute/drejtori-jusuf.jpg',
    role: sq.about.directorRole,
    bio: sq.about.directorBio,
    credentials: [
        'Imam Muhammad ibn Saud Islamic University',
        'University of Ez-Zitouna, Tunisia — Higher Institute of Islamic Civilization',
        'Përkthyes i shumë veprave fetare e edukative'
    ]
}

export const kurani = {
    url: 'https://www.kurani.info',
    displayUrl: 'kurani.info',
    purpose:
        'Qëllimi i kësaj platforme është t’ua ofrojë lexuesve shqiptarë Kuranin Fisnik përmes tekstit origjinal arab dhe përkthimit të kuptimeve të tij në gjuhën shqipe.',
    translator: {
        name: 'Jusuf H. Kastrati',
        role: sq.kurani.translatorRole
    },
    team: [
        { name: 'Dr. Arben Ahmetaj', role: 'religious' },
        { name: 'MSc. Burim Koçinaj', role: 'religious' },
        { name: 'Dr. Mustafë Jakupi', role: 'religious' },
        { name: 'Dr. Rijad Misini', role: 'religious' },
        // Hidden until the editing is finished: { name: 'Dr. Muhamed ibn Abdulaziz el-Hudejri', role: 'consulting' },
        { name: 'MSc. Florian Leli', role: 'consulting' },
        { name: 'MSc. Jeton Koqinaj', role: 'language' },
        // Hidden until the editing is finished: { name: 'MSc. Jonis Y. Buzi', role: 'language' }
    ] as { name: string; role: keyof Messages['kurani']['roles'] }[]
}

export const getStats = (t: Messages): Stat[] => [
    { value: '6+', label: t.home.stats.titles, source: 'titles' },
    { value: '3', label: t.home.stats.languages, source: 'languages' },
    { value: '4', label: t.home.stats.fields },
    { value: '24/7', label: t.home.stats.online }
]

export const contact = {
    email: 'institutigerma@gmail.com'
}

export const socials: SocialLink[] = [
    {
        label: 'Facebook',
        href: 'https://www.facebook.com/InstitutiGerma/',
        icon: Facebook
    },
    {
        label: 'Instagram',
        href: 'https://www.instagram.com/instituti_germa/',
        icon: Instagram
    },
    {
        label: 'Telegram',
        href: 'https://t.me/institutigerma',
        icon: Send
    },
    {
        label: 'Email',
        href: 'mailto:institutigerma@gmail.com',
        icon: Mail
    }
]

/** Share button labels, keyed by language code (sq, en, de, es, tr, ar, bs). */
export const shareLabels: Record<string, ShareLabels> = Object.fromEntries(
    (Object.keys(messages) as Locale[]).map((code) => [code, messages[code].share])
)

/** Labels for the book detail page in Albanian. */
export const defaultBookPageLabels: BookPageLabels = sq.bookPage

/** Labels for the book detail page, keyed by the book's `language` value (Anglisht, Gjermanisht, ...). */
export const bookPageLabels: Record<string, BookPageLabels> = Object.fromEntries(
    (Object.keys(messages) as Locale[]).map((code) => [localeMeta[code].bookLanguage, messages[code].bookPage])
)
